
import { createClient } from '@supabase/supabase-js';
import { v4 as uuidv4 } from 'uuid';

// Initialize Supabase client
export const supabase = createClient(
  'https://btqerswfdpgcuozfhaow.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJ0cWVyc3dmZHBnY3VvemZoYW93Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDczNDMwNTcsImV4cCI6MjA2MjkxOTA1N30.xKgVNMZSShjw4DfshuO2sK9_QUla2KcrQ5oxjuxhmEQ'
);

// Define TeamMember type with all required properties
export interface TeamMember {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  project_id?: string;
  is_placeholder?: boolean;
  user_id?: string;
  avatar_url?: string;
  is_active?: boolean;
  password?: string; // Make password optional for TeamMembers interface
}

// Define additional types needed by the components
export interface Profile {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  role?: string;
}

export interface ProjectLeadContact {
  id: string;
  lead_id: string;
  name: string;
  email?: string;
  phone?: string;
  role?: string;
  notes?: string;
  is_primary?: boolean;
  created_at: string;
}

export interface ProjectLeadCommunication {
  id: string;
  lead_id: string;
  type: string;
  content?: string;
  date: string;
  user_id: string;
  follow_up_date?: string;
  created_at: string;
}

export interface SupplierCategory {
  id: string;
  name: string;
  count?: number;
  total_amount?: number;
  actual_spend?: number; // Add actual_spend property
}

export interface SupplierPerformance {
  project_id: string;
  project_name: string;
  total_budget: number;
  actual_spend: number;
  overspend_percentage: number;
  average_rating: number;
}

// Add new types for the enhanced lead functionality
export interface ProjectLeadDocument {
  id: string;
  lead_id: string;
  file_name: string;
  file_type?: string;
  file_size?: number;
  storage_path: string;
  category?: string;
  description?: string;
  uploaded_by: string;
  uploaded_at: string;
  created_at: string;
}

export interface ProjectLeadUrl {
  id: string;
  lead_id: string;
  url: string;
  title?: string;
  description?: string;
  category?: string;
  favicon_url?: string;
  screenshot_url?: string;
  added_by: string;
  added_at: string;
  created_at: string;
}

// Update ProjectLead interface to include new fields
export interface ProjectLead {
  id: string;
  name: string;
  company_name?: string;
  description?: string;
  status: string;
  estimated_value?: number;
  estimated_value_frequency?: string;
  estimated_start_date?: string;
  assigned_to?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  confidence_level?: number;
  project_timeline?: string;
  budget_range?: string;
  decision_makers?: string[];
  key_requirements?: string;
  competitors?: string[];
  technical_requirements?: string;
  next_steps?: string;
}

// Fetch team members from the database
export async function fetchTeamMembers(): Promise<TeamMember[]> {
  try {
    const { data, error } = await supabase
      .from('team_members')
      .select('id, first_name, last_name, email, role, project_id, is_placeholder, user_id, avatar_url, is_active')
      .order('first_name');
    
    if (error) {
      console.error('Error fetching team members:', error);
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error('Error in fetchTeamMembers:', error);
    return [];
  }
}

// Alias for fetchTeamMembers (to support existing code)
export const fetchGlobalTeamMembers = fetchTeamMembers;

// Fetch project-specific team members
export async function fetchProjectTeamMembers(projectId: string): Promise<TeamMember[]> {
  try {
    const { data, error } = await supabase
      .from('team_members')
      .select('*')
      .eq('project_id', projectId)
      .order('first_name');
    
    if (error) {
      console.error('Error fetching project team members:', error);
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error('Error in fetchProjectTeamMembers:', error);
    return [];
  }
}

// Update team member
export async function updateTeamMember(id: string, updates: Partial<TeamMember>): Promise<TeamMember> {
  try {
    const { data, error } = await supabase
      .from('team_members')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error('Error updating team member:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in updateTeamMember:', error);
    throw error;
  }
}

// Delete team member
export async function deleteTeamMember(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('team_members')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('Error deleting team member:', error);
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error('Error in deleteTeamMember:', error);
    throw error;
  }
}

// Create team member - update the function signature to accept only one parameter (teamMember)
export async function createTeamMember(teamMember: Omit<TeamMember, 'id'> & { password?: string }): Promise<TeamMember> {
  try {
    const { data, error } = await supabase
      .from('team_members')
      .insert([{ ...teamMember, id: uuidv4() }])
      .select()
      .single();
    
    if (error) {
      console.error('Error creating team member:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in createTeamMember:', error);
    throw error;
  }
}

// Remove team member from project
export async function removeTeamMember(memberId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('team_members')
      .delete()
      .eq('id', memberId);
    
    if (error) {
      console.error('Error removing team member:', error);
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error('Error in removeTeamMember:', error);
    throw error;
  }
}

// Add current user to project - update to accept projectId and role
export async function addCurrentUserToProject(
  projectId: string, 
  role: string = 'member'
): Promise<TeamMember> {
  try {
    const { data, error } = await supabase
      .from('team_members')
      .insert([{ 
        id: uuidv4(),
        project_id: projectId,
        role 
      }])
      .select()
      .single();
    
    if (error) {
      console.error('Error adding user to project:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in addCurrentUserToProject:', error);
    throw error;
  }
}

// Add a team member to a project - fix function signature to properly handle user IDs
export async function addTeamMember(
  projectId: string, 
  memberIdOrUserId: string, 
  role: string = 'member'
): Promise<TeamMember> {
  try {
    console.log('Adding team member:', { projectId, memberIdOrUserId, role });
    
    // First, check if the passed ID is already a team member ID from the available team members
    const { data: existingMember, error: lookupError } = await supabase
      .from('team_members')
      .select('*')
      .eq('id', memberIdOrUserId)
      .maybeSingle();
    
    console.log('Existing member lookup result:', { existingMember, lookupError });
    
    if (existingMember && !lookupError) {
      // This is a team member ID - check if it's a global member (project_id is null)
      if (existingMember.project_id === null) {
        console.log('Found global team member, updating to assign to project...');
        
        // Check if this email already exists in the target project
        if (existingMember.email) {
          const { data: existingProjectMemberByEmail } = await supabase
            .from('team_members')
            .select('id')
            .eq('project_id', projectId)
            .ilike('email', existingMember.email)
            .maybeSingle();
          
          if (existingProjectMemberByEmail) {
            throw new Error('A team member with this email already exists in this project');
          }
        }
        
        // Check if this user_id already exists in the target project (only if user_id is not null)
        if (existingMember.user_id) {
          const { data: existingProjectMemberByUserId } = await supabase
            .from('team_members')
            .select('id')
            .eq('project_id', projectId)
            .eq('user_id', existingMember.user_id)
            .maybeSingle();
          
          if (existingProjectMemberByUserId) {
            throw new Error('User is already a member of this project');
          }
        }
        
        // Update the existing global member to assign them to the project
        const { data: updatedMember, error: updateError } = await supabase
          .from('team_members')
          .update({
            project_id: projectId,
            role,
            updated_at: new Date().toISOString()
          })
          .eq('id', memberIdOrUserId)
          .select()
          .single();
        
        if (updateError) {
          console.error('Error updating global team member:', updateError);
          throw updateError;
        }
        
        console.log('Successfully updated global team member:', updatedMember);
        return updatedMember;
      } else {
        // This member already has a project assignment, need to copy their data to the new project
        console.log('Found existing team member with project assignment, checking for duplicates in project...');
        
        // Check if a team member with this email already exists in the project (if email exists)
        if (existingMember.email) {
          const { data: existingProjectMemberByEmail } = await supabase
            .from('team_members')
            .select('id')
            .eq('project_id', projectId)
            .ilike('email', existingMember.email)
            .maybeSingle();
          
          console.log('Email duplicate check result:', existingProjectMemberByEmail);
          
          if (existingProjectMemberByEmail) {
            throw new Error('A team member with this email already exists in this project');
          }
        }
        
        // Check if a team member with this user_id already exists in the project (only if user_id is not null)
        if (existingMember.user_id) {
          const { data: existingProjectMemberByUserId } = await supabase
            .from('team_members')
            .select('id')
            .eq('project_id', projectId)
            .eq('user_id', existingMember.user_id)
            .maybeSingle();
          
          console.log('User ID duplicate check result:', existingProjectMemberByUserId);
          
          if (existingProjectMemberByUserId) {
            throw new Error('User is already a member of this project');
          }
        }
        
        console.log('No duplicates found, creating new project team member...');
        
        // Prepare the new team member data - explicitly handle null user_id
        const newTeamMemberData: any = {
          id: uuidv4(),
          project_id: projectId,
          email: existingMember.email,
          first_name: existingMember.first_name,
          last_name: existingMember.last_name,
          role,
          is_placeholder: existingMember.is_placeholder || false
        };
        
        // Only add user_id if it's not null - this prevents foreign key constraint issues
        if (existingMember.user_id !== null && existingMember.user_id !== undefined) {
          newTeamMemberData.user_id = existingMember.user_id;
          console.log('Including user_id in new team member data:', existingMember.user_id);
        } else {
          console.log('user_id is null/undefined, treating as placeholder');
          newTeamMemberData.is_placeholder = true;
        }
        
        console.log('Final team member data to insert:', newTeamMemberData);
        
        const { data, error } = await supabase
          .from('team_members')
          .insert([newTeamMemberData])
          .select()
          .single();
        
        if (error) {
          console.error('Error adding team member:', error);
          throw error;
        }
        
        console.log('Successfully added team member:', data);
        return data;
      }
    } else {
      // Check if the passed ID is a user_id by checking the profiles table
      console.log('Not a team member ID, checking if it\'s a user ID...');
      
      const { data: profileExists, error: profileError } = await supabase
        .from('profiles')
        .select('id, email, first_name, last_name')
        .eq('id', memberIdOrUserId)
        .maybeSingle();
      
      console.log('Profile lookup result:', { profileExists, profileError });
      
      if (profileExists && !profileError) {
        // Check if they're already in this project by user_id
        const { data: existingProjectMemberByUserId } = await supabase
          .from('team_members')
          .select('id')
          .eq('project_id', projectId)
          .eq('user_id', memberIdOrUserId)
          .maybeSingle();
        
        console.log('User ID duplicate check in project:', existingProjectMemberByUserId);
        
        if (existingProjectMemberByUserId) {
          throw new Error('User is already a member of this project');
        }
        
        // Also check by email to prevent duplicate emails (case-insensitive)
        if (profileExists.email) {
          const { data: existingByEmail } = await supabase
            .from('team_members')
            .select('id')
            .eq('project_id', projectId)
            .ilike('email', profileExists.email)
            .maybeSingle();
          
          console.log('Email duplicate check in project:', existingByEmail);
          
          if (existingByEmail) {
            throw new Error('A team member with this email already exists in this project');
          }
        }
        
        console.log('No duplicates found, adding user to project...');
        
        // Add them to the project using their profile data
        const { data, error } = await supabase
          .from('team_members')
          .insert([{ 
            id: uuidv4(),
            project_id: projectId,
            user_id: memberIdOrUserId,
            email: profileExists.email,
            first_name: profileExists.first_name,
            last_name: profileExists.last_name,
            role 
          }])
          .select()
          .single();
        
        if (error) {
          console.error('Error adding team member:', error);
          throw error;
        }
        
        console.log('Successfully added user to project:', data);
        return data;
      } else {
        throw new Error('Cannot find a valid team member or user with the provided ID');
      }
    }
  } catch (error) {
    console.error('Error in addTeamMember:', error);
    throw error;
  }
}

// Create a new project lead
export async function createProjectLead(lead: Omit<ProjectLead, 'id' | 'created_at' | 'updated_at'>): Promise<ProjectLead> {
  try {
    // Validate that created_by is provided
    if (!lead.created_by) {
      throw new Error('created_by is required to create a lead');
    }
    
    // Add id using uuid
    const leadWithId = {
      ...lead,
      id: uuidv4()
    };
    
    const { data, error } = await supabase
      .from('project_leads')
      .insert([leadWithId])
      .select()
      .single();
    
    if (error) {
      console.error('Error creating project lead:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in createProjectLead:', error);
    throw error;
  }
}

// Update an existing project lead
export async function updateProjectLead(
  id: string, 
  lead: Partial<Omit<ProjectLead, 'id' | 'created_at' | 'updated_at' | 'created_by'>>
): Promise<ProjectLead> {
  try {
    const { data, error } = await supabase
      .from('project_leads')
      .update(lead)
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error('Error updating project lead:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in updateProjectLead:', error);
    throw error;
  }
}

// Delete a project lead
export async function deleteProjectLead(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('project_leads')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('Error deleting project lead:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in deleteProjectLead:', error);
    throw error;
  }
}

// Add a team member directly to the database (renamed from the duplicate addTeamMember)
export async function createTeamMemberRecord(teamMember: Omit<TeamMember, 'id'>): Promise<TeamMember> {
  try {
    const { data, error } = await supabase
      .from('team_members')
      .insert([{ ...teamMember, id: uuidv4() }])
      .select()
      .single();
    
    if (error) {
      console.error('Error adding team member:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in createTeamMemberRecord:', error);
    throw error;
  }
}

// Supplier-related functions
export async function fetchAllSuppliers() {
  try {
    const { data, error } = await supabase
      .from('suppliers')
      .select('*')
      .order('name');
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching suppliers:', error);
    return [];
  }
}

export async function fetchProjectSuppliers(projectId: string) {
  try {
    const { data, error } = await supabase
      .from('project_suppliers')
      .select('*, supplier:supplier_id(*)')
      .eq('project_id', projectId);
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching project suppliers:', error);
    return [];
  }
}

export async function createSupplier(supplier: any) {
  try {
    const { data, error } = await supabase
      .from('suppliers')
      .insert([{ ...supplier, id: uuidv4() }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating supplier:', error);
    throw error;
  }
}

export async function updateSupplier(id: string, updates: any) {
  try {
    const { data, error } = await supabase
      .from('suppliers')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating supplier:', error);
    throw error;
  }
}

export async function deleteSupplier(id: string) {
  try {
    const { error } = await supabase
      .from('suppliers')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting supplier:', error);
    throw error;
  }
}

export async function addSupplierToProject(projectId: string, supplierId: string, notes?: string) {
  try {
    const { data, error } = await supabase
      .from('project_suppliers')
      .insert([{ 
        id: uuidv4(),
        project_id: projectId,
        supplier_id: supplierId,
        notes
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding supplier to project:', error);
    throw error;
  }
}

export async function removeSupplierFromProject(id: string) {
  try {
    const { error } = await supabase
      .from('project_suppliers')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error removing supplier from project:', error);
    throw error;
  }
}

export async function addSupplierFeedback(feedback: any) {
  try {
    const { data, error } = await supabase
      .from('supplier_feedback')
      .insert([{ ...feedback, id: uuidv4() }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding supplier feedback:', error);
    throw error;
  }
}

export async function getSupplierPerformance(supplierId: string) {
  try {
    const { data, error } = await supabase
      .rpc('get_supplier_performance', { p_supplier_id: supplierId });
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting supplier performance:', error);
    return [];
  }
}

// Supplier-related functions - Updated to fetch actual data based on supplierId
export async function getSupplierCategories(supplierId: string): Promise<SupplierCategory[]> {
  try {
    // Use the new database function that includes actual spend
    const { data, error } = await supabase
      .rpc('get_supplier_categories', { p_supplier_id: supplierId });
    
    if (error) {
      console.error('Error fetching supplier categories:', error);
      throw error;
    }
    
    // Transform the data to match SupplierCategory interface
    return (data || []).map((item: any) => ({
      id: item.name, // Use name as id since we don't have a separate id
      name: item.name,
      count: item.count || 0,
      total_amount: item.total_amount || 0,
      actual_spend: item.actual_spend || 0
    }));
  } catch (error) {
    console.error('Error in getSupplierCategories:', error);
    return [];
  }
}

// Lead-related functions for contacts and communications
export async function fetchLeadContacts(leadId: string): Promise<ProjectLeadContact[]> {
  try {
    const { data, error } = await supabase
      .from('project_lead_contacts')
      .select('*')
      .eq('lead_id', leadId)
      .order('name');
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching lead contacts:', error);
    return [];
  }
}

export async function createLeadContact(contact: Omit<ProjectLeadContact, 'id' | 'created_at'>): Promise<ProjectLeadContact> {
  try {
    const { data, error } = await supabase
      .from('project_lead_contacts')
      .insert([{ ...contact, id: uuidv4() }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating lead contact:', error);
    throw error;
  }
}

export async function updateLeadContact(id: string, updates: Partial<ProjectLeadContact>): Promise<ProjectLeadContact> {
  try {
    const { data, error } = await supabase
      .from('project_lead_contacts')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating lead contact:', error);
    throw error;
  }
}

export async function deleteLeadContact(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('project_lead_contacts')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting lead contact:', error);
    throw error;
  }
}

export async function fetchLeadCommunications(leadId: string): Promise<ProjectLeadCommunication[]> {
  try {
    const { data, error } = await supabase
      .from('project_lead_communications')
      .select('*')
      .eq('lead_id', leadId)
      .order('date', { ascending: false });
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching lead communications:', error);
    return [];
  }
}

export async function addLeadCommunication(communication: Omit<ProjectLeadCommunication, 'id' | 'created_at'>): Promise<ProjectLeadCommunication> {
  try {
    const { data, error } = await supabase
      .from('project_lead_communications')
      .insert([{ ...communication, id: uuidv4() }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding lead communication:', error);
    throw error;
  }
}

export async function updateLeadCommunication(id: string, updates: Partial<ProjectLeadCommunication>): Promise<ProjectLeadCommunication> {
  try {
    const { data, error } = await supabase
      .from('project_lead_communications')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating lead communication:', error);
    throw error;
  }
}

export async function deleteLeadCommunication(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('project_lead_communications')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting lead communication:', error);
    throw error;
  }
}

export async function convertLeadToProject(leadId: string): Promise<string> {
  try {
    const { data, error } = await supabase
      .rpc('convert_lead_to_project', { lead_uuid: leadId });
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error converting lead to project:', error);
    throw error;
  }
}

// Document management functions
export const uploadLeadDocument = async (
  leadId: string,
  file: File,
  category: string = 'general',
  description: string = ''
): Promise<ProjectLeadDocument> => {
  try {
    // Get any available team member instead of relying on auth
    const { data: teamMembers, error: teamMembersError } = await supabase
      .from('team_members')
      .select('id, user_id')
      .limit(1);

    if (teamMembersError || !teamMembers || teamMembers.length === 0) {
      console.error('No team members found:', teamMembersError);
      throw new Error('Unable to identify current user');
    }

    // Use the first available team member ID as a fallback
    const teamMemberId = teamMembers[0].id;
    
    console.log('Using team member ID for document upload:', teamMemberId);

    // Import the path utilities with correct path
    const { createUniqueFileName } = await import('../../utils/pathUtils');
    
    // Create a safe, unique filename
    const safeFileName = createUniqueFileName(file.name);
    const filePath = `${leadId}/${safeFileName}`;

    console.log('Uploading file to path:', filePath);
    console.log('File size:', file.size, 'bytes');

    // Upload file to storage with better error handling
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('lead-documents')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      console.error('Storage upload error:', uploadError);
      throw new Error(`File upload failed: ${uploadError.message}`);
    }

    if (!uploadData?.path) {
      throw new Error('Upload completed but no file path returned');
    }

    console.log('File uploaded successfully to:', uploadData.path);

    // Create document record
    const { data: document, error: dbError } = await supabase
      .from('project_lead_documents')
      .insert({
        id: uuidv4(),
        lead_id: leadId,
        file_name: file.name,
        file_type: file.type,
        file_size: file.size,
        storage_path: uploadData.path,
        category,
        description,
        uploaded_by: teamMemberId
      })
      .select()
      .single();

    if (dbError) {
      console.error('Database insert error:', dbError);
      // Try to clean up the uploaded file if database insert fails
      await supabase.storage
        .from('lead-documents')
        .remove([uploadData.path]);
      throw new Error(`Failed to save document record: ${dbError.message}`);
    }

    console.log('Document record created successfully:', document);
    return document;
  } catch (error) {
    console.error('Error in uploadLeadDocument:', error);
    throw error;
  }
};

export const deleteLeadDocument = async (documentId: string): Promise<void> => {
  try {
    // Get the document details first
    const { data: document, error: fetchError } = await supabase
      .from('project_lead_documents')
      .select('storage_path')
      .eq('id', documentId)
      .single();

    if (fetchError) {
      throw fetchError;
    }

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('lead-documents')
      .remove([document.storage_path]);

    if (storageError) {
      console.warn('Failed to delete file from storage:', storageError);
    }

    // Delete database record
    const { error: dbError } = await supabase
      .from('project_lead_documents')
      .delete()
      .eq('id', documentId);

    if (dbError) {
      throw dbError;
    }
  } catch (error) {
    console.error('Error deleting document:', error);
    throw error;
  }
};

export const fetchLeadDocuments = async (leadId: string): Promise<ProjectLeadDocument[]> => {
  const { data, error } = await supabase
    .from('project_lead_documents')
    .select('*')
    .eq('lead_id', leadId)
    .order('uploaded_at', { ascending: false });

  if (error) {
    throw error;
  }

  return data || [];
};

export const getLeadDocumentUrl = (storagePath: string): string => {
  const { data } = supabase.storage
    .from('lead-documents')
    .getPublicUrl(storagePath);
  
  return data.publicUrl;
};

// URL management functions
export const fetchLeadUrls = async (leadId: string): Promise<ProjectLeadUrl[]> => {
  const { data, error } = await supabase
    .from('project_lead_urls')
    .select('*')
    .eq('lead_id', leadId)
    .order('added_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const createLeadUrl = async (data: {
  lead_id: string;
  url: string;
  title?: string;
  description?: string;
  category?: string;
}): Promise<void> => {
  try {
    // Instead of using supabase.auth.getUser(), we need to get the current team member
    // from the context or use a different approach since this project uses custom auth
    
    // For now, let's get the team member ID from the current user context
    // This assumes we have access to the current user's team member ID
    
    // Get all team members and find one that can create URLs (temporary solution)
    const { data: teamMembers, error: teamMembersError } = await supabase
      .from('team_members')
      .select('id, user_id')
      .limit(1);

    if (teamMembersError || !teamMembers || teamMembers.length === 0) {
      console.error('No team members found:', teamMembersError);
      throw new Error('Unable to identify current user');
    }

    // Use the first available team member ID as a fallback
    // This should be replaced with proper user context management
    const teamMemberId = teamMembers[0].id;
    
    console.log('Using team member ID:', teamMemberId);

    const { error } = await supabase
      .from('project_lead_urls')
      .insert({
        ...data,
        added_by: teamMemberId,
      });

    if (error) {
      console.error('Error inserting URL:', error);
      throw error;
    }

    console.log('URL created successfully');
  } catch (error) {
    console.error('Error in createLeadUrl:', error);
    throw error;
  }
};

export const updateLeadUrl = async (urlId: string, updates: Partial<ProjectLeadUrl>): Promise<void> => {
  const { error } = await supabase
    .from('project_lead_urls')
    .update(updates)
    .eq('id', urlId);

  if (error) throw error;
};

export const deleteLeadUrl = async (urlId: string): Promise<void> => {
  const { error } = await supabase
    .from('project_lead_urls')
    .delete()
    .eq('id', urlId);

  if (error) throw error;
};

// Project tasks functions
export async function fetchProjectTasks(projectId: string) {
  try {
    const { data, error } = await supabase
      .from('project_tasks')
      .select('*')
      .eq('project_id', projectId)
      .order('position');
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching project tasks:', error);
    return [];
  }
}

export async function deleteProjectTask(id: string) {
  try {
    const { error } = await supabase
      .from('project_tasks')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting project task:', error);
    throw error;
  }
}

// Chat-related functions - Update fetchProjectChatMessages to include team member details
export async function fetchProjectChatMessages(projectId: string) {
  try {
    const { data, error } = await supabase
      .from('project_chat_messages')
      .select(`
        *,
        team_members (
          id,
          first_name,
          last_name,
          email,
          role,
          avatar_url
        )
      `)
      .eq('project_id', projectId)
      .is('thread_id', null)
      .order('created_at');
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching chat messages:', error);
    return [];
  }
}

export async function fetchMessageReactions(messageId: string) {
  try {
    const { data, error } = await supabase
      .from('project_chat_reactions')
      .select('*')
      .eq('message_id', messageId);
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching message reactions:', error);
    return [];
  }
}

export async function fetchThreadMessages(threadId: string) {
  try {
    const { data, error } = await supabase
      .from('project_chat_messages')
      .select(`
        *,
        team_members (
          id, 
          first_name,
          last_name,
          email,
          role,
          avatar_url
        )
      `)
      .eq('thread_id', threadId)
      .order('created_at');
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching thread messages:', error);
    return [];
  }
}

// Task comment functions
export async function createTaskComment(comment: any) {
  try {
    const { data, error } = await supabase
      .from('project_task_comments')
      .insert([{ ...comment, id: uuidv4() }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating task comment:', error);
    throw error;
  }
}

export async function fetchTaskComments(taskId: string) {
  try {
    const { data, error } = await supabase
      .from('project_task_comments')
      .select(`
        *,
        team_member:team_member_id (
          id,
          first_name,
          last_name,
          email,
          avatar_url
        )
      `)
      .eq('task_id', taskId)
      .order('created_at');
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching task comments:', error);
    return [];
  }
}

// New function to get comment counts for tasks
export async function fetchTaskCommentCounts(taskIds: string[]) {
  if (!taskIds || taskIds.length === 0) return {};
  
  try {
    const { data, error } = await supabase
      .from('project_task_comments')
      .select('task_id')
      .in('task_id', taskIds);
    
    if (error) throw error;
    
    // Transform the results into a map of taskId -> count
    const countMap: Record<string, number> = {};
    
    if (data) {
      // Group by task_id and count
      data.forEach(comment => {
        if (!countMap[comment.task_id]) {
          countMap[comment.task_id] = 0;
        }
        countMap[comment.task_id]++;
      });
      
      return countMap;
    }
    
    return countMap;
  } catch (error) {
    console.error('Error fetching task comment counts:', error);
    return {};
  }
}

// Utility functions
export function formatProjectId(id: string) {
  // Returns a formatted project ID (e.g., PROJ-1234)
  return `PROJ-${id.substring(0, 4)}`;
}

export async function fetchTeamProfiles(): Promise<Profile[]> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('first_name');
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching team profiles:', error);
    return [];
  }
}

// Fetch project leads
export async function fetchProjectLeads(): Promise<ProjectLead[]> {
  try {
    const { data, error } = await supabase
      .from('project_leads')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching project leads:', error);
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error('Error in fetchProjectLeads:', error);
    return [];
  }
}

// Mood Boards functions
export async function fetchMoodBoards(projectId: string) {
  try {
    const { data, error } = await supabase
      .from('project_mood_boards')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching mood boards:', error);
    return [];
  }
}

export async function createMoodBoard(moodBoard: any) {
  try {
    const { data, error } = await supabase
      .from('project_mood_boards')
      .insert([{ ...moodBoard, id: uuidv4() }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating mood board:', error);
    throw error;
  }
}

export async function updateMoodBoard(id: string, updates: any) {
  try {
    const { data, error } = await supabase
      .from('project_mood_boards')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating mood board:', error);
    throw error;
  }
}

export async function deleteMoodBoard(id: string) {
  try {
    const { error } = await supabase
      .from('project_mood_boards')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting mood board:', error);
    throw error;
  }
}

export async function fetchMoodBoardItems(moodBoardId: string) {
  try {
    const { data, error } = await supabase
      .from('mood_board_items')
      .select('*')
      .eq('mood_board_id', moodBoardId)
      .order('z_index');
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching mood board items:', error);
    return [];
  }
}

export async function createMoodBoardItem(item: any) {
  try {
    const { data, error } = await supabase
      .from('mood_board_items')
      .insert([{ ...item, id: uuidv4() }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating mood board item:', error);
    throw error;
  }
}

export async function updateMoodBoardItem(id: string, updates: any) {
  try {
    const { data, error } = await supabase
      .from('mood_board_items')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating mood board item:', error);
    throw error;
  }
}

export async function deleteMoodBoardItem(id: string) {
  try {
    const { error } = await supabase
      .from('mood_board_items')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting mood board item:', error);
    throw error;
  }
}

// Discovery Form Functions
export interface DiscoveryFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company_name: string;
  position: string;
  project_type: string;
  project_description: string;
  pain_points: string;
  success_metrics: string;
  target_audience: string;
  brand_guidelines: string;
  competitive_landscape: string;
  estimated_budget: number | null;
  budget_frequency: string;
  desired_start_date: string | null;
  project_deadline: string | null;
  reference_urls: string[];
  inspiration_file_paths: string[];
  additional_notes: string;
  team_member_id?: string; // Add team member field for our custom auth
}

export const createDiscoveryForm = async (formData: {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company_name: string;
  position: string;
  project_type: string;
  project_description: string;
  pain_points: string;
  success_metrics: string;
  target_audience: string;
  brand_guidelines: string;
  competitive_landscape: string;
  estimated_budget: number | null;
  budget_frequency: string;
  desired_start_date: string | null;
  project_deadline: string | null;
  reference_urls: string[];
  inspiration_file_paths: string[];
  additional_notes: string;
}) => {
  const { data, error } = await supabase
    .from('discovery_forms')
    .insert({
      contact_name: `${formData.first_name} ${formData.last_name}`,
      contact_email: formData.email,
      contact_phone: formData.phone,
      company_name: formData.company_name,
      contact_role: formData.position,
      project_type: formData.project_type, // Fix: properly save project_type
      project_name: formData.project_type, // Keep this for backward compatibility if needed
      project_description: formData.project_description,
      project_objectives: formData.project_description, // Map to required field
      client_pain_points: formData.pain_points,
      success_metrics: formData.success_metrics,
      target_audience: formData.target_audience,
      brand_guidelines: formData.brand_guidelines,
      competitor_analysis: formData.competitive_landscape,
      estimated_budget_range: formData.estimated_budget?.toString() || '',
      project_timeline: formData.budget_frequency,
      preferred_start_date: formData.desired_start_date,
      reference_urls: formData.reference_urls,
      inspiration_file_paths: formData.inspiration_file_paths,
      inspiration_description: formData.additional_notes, // Map to correct field
      status: 'submitted'
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating discovery form:', error);
    throw error;
  }

  return data;
};

export const fetchDiscoveryForms = async () => {
  const { data, error } = await supabase
    .from('discovery_forms')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching discovery forms:', error);
    throw error;
  }

  return data;
};
